import { Modal } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { type FirstLoginValues, firstLoginSchema } from "@/src/auth/model/first-login.schema";
import { refreshProfile } from "@/src/auth/store/auth.thunks";
import { Button } from "@/src/common/components/ui/button";
import { InputGroup } from "@/src/common/components/ui/input";
import { useAppDispatch } from "@/src/common/redux/store";
import { supabase } from "@/src/common/services/supabase.service";
import { toastService } from "@/src/common/services/toast.service";
import { AvatarPicker } from "@/src/profile/components/avatar-picker";
import { AvatarUploadError, avatarUploadService } from "@/src/profile/services/avatar-upload.service";

function fullName(first: string | null | undefined, last: string | null | undefined): string {
	return [first, last].filter(Boolean).join(" ").trim();
}

export function FirstLoginModal() {
	const { t } = useTranslation("auth");
	const { t: tCommon } = useTranslation("common");
	const { profile } = useAuth();
	const dispatch = useAppDispatch();

	const isOpen = Boolean(profile && profile.profile_completed_at === null);

	const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const { control, handleSubmit, reset, formState } = useForm<FirstLoginValues>({
		resolver: zodResolver(firstLoginSchema),
		mode: "onChange",
		defaultValues: { first_name: "", last_name: "" },
	});

	useEffect(() => {
		if (!profile) return;
		reset({ first_name: profile.first_name ?? "", last_name: profile.last_name ?? "" });
	}, [profile, reset]);

	useEffect(() => {
		return () => {
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		};
	}, [previewUrl]);

	const handlePickAvatar = (file: File) => {
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		setPreviewUrl(URL.createObjectURL(file));
		setPendingAvatar(file);
	};

	const onSubmit = handleSubmit(async (values) => {
		if (!profile) return;
		try {
			let avatarUrl: string | undefined;
			if (pendingAvatar) {
				avatarUrl = await avatarUploadService.uploadWithoutPersist(profile.id, pendingAvatar);
			}
			const { error } = await supabase
				.from("profiles")
				.update({
					first_name: values.first_name.trim(),
					last_name: values.last_name.trim(),
					profile_completed_at: new Date().toISOString(),
					...(avatarUrl ? { avatar_url: avatarUrl } : null),
				})
				.eq("id", profile.id);
			if (error) throw new Error(error.message);
			await dispatch(refreshProfile());
			toastService.success(t("firstLogin.success"));
		} catch (err) {
			console.error("[auth] first-login save failed", err);
			toastService.error(err instanceof AvatarUploadError ? t("firstLogin.avatar_unsupported") : t("firstLogin.error"));
		}
	});

	if (!profile) return null;

	return (
		<Modal.Backdrop isOpen={isOpen} isKeyboardDismissDisabled isDismissable={false} onOpenChange={() => undefined}>
			<Modal.Container>
				<Modal.Dialog className="sm:max-w-[480px]">
					<Modal.Header>
						<Modal.Heading>{t("firstLogin.title")}</Modal.Heading>
						<p className="mt-1 text-sm text-muted-foreground">{t("firstLogin.subtitle")}</p>
					</Modal.Header>
					<Modal.Body>
						<form id="first-login-form" onSubmit={onSubmit} className="flex flex-col gap-4 p-0.5" noValidate>
							<div className="flex justify-center">
								<AvatarPicker
									src={profile.avatar_url}
									name={fullName(profile.first_name, profile.last_name) || profile.email}
									previewSrc={previewUrl}
									onPick={handlePickAvatar}
									ariaLabel={tCommon("aria.change_avatar")}
								/>
							</div>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<InputGroup
									control={control}
									name="first_name"
									label={t("firstLogin.first_name")}
									placeholder={t("firstLogin.first_name_placeholder")}
									isRequired
								/>
								<InputGroup
									control={control}
									name="last_name"
									label={t("firstLogin.last_name")}
									placeholder={t("firstLogin.last_name_placeholder")}
									isRequired
								/>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-xs font-medium text-foreground">{t("firstLogin.email")}</span>
								<span className="text-sm text-muted-foreground">{profile.email}</span>
							</div>
						</form>
					</Modal.Body>
					<Modal.Footer>
						<Button
							type="submit"
							form="first-login-form"
							variant="primary"
							isDisabled={!formState.isValid || formState.isSubmitting}
							isPending={formState.isSubmitting}
						>
							{t("firstLogin.submit")}
						</Button>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
